import { useState } from 'react';

import { mapQuery } from 'utils/query';
import { useAppSelector } from 'store';
import { Flex } from 'components/flex';
import { formatDate } from 'utils/date';
import { formatPrice } from 'utils/helpers';
import { ListCard } from 'components/list-card';
import { PayoutType, PayoutStatus } from 'types';
import { ListHeader } from 'components/list-header';
import { Payout, useListQuery } from 'store/api/payout';
import { useFilter, useShowMore, useSearchQuery } from 'hooks';
import { PayoutStatusBadge } from 'components/payout-status-badge';
import { RemovePayoutDialog } from 'components/remove-payout-dialog';
import { ApprovalPayoutDialog } from 'components/approval-payout-dialog';
import {
  approveReimbursementRoles,
  canDeleteReimbursementStatuses,
} from 'config';
import {
  isAuthCompanyUser,
  isEventManagerRole,
  selectAuthUserRole,
} from 'store/slices/auth';

import * as CommonStyles from '../styles';

import { ReimbursementProps } from './types';
import { CreateReimbursement } from './create';
import { ReimbursementDrawerDetails } from './details';

const titles = [
  'Date',
  'Employee',
  'Description',
  'Amount',
  'Category',
  'Status',
];

const getTemplateAndTitles = (canApproveReimbursement: boolean) => {
  let template = 'repeat(6, 1fr)';
  const currentTitles = [...titles];

  if (canApproveReimbursement) {
    template += ' 184px';
    currentTitles.push('Approve');
  }

  return { template, currentTitles };
};

export const Reimbursement = ({ event }: ReimbursementProps) => {
  const isManager = useAppSelector(isEventManagerRole);
  const authUserRole = useAppSelector(selectAuthUserRole);
  const isCompanyOwner = useAppSelector(isAuthCompanyUser);
  const canApproveReimbursement =
    isCompanyOwner ||
    !!(authUserRole && approveReimbursementRoles.includes(authUserRole));

  const [selectedReimbursementId, setSelectedReimbursementId] =
    useState<number>();

  const { debounceQuery, ...search } = useSearchQuery();
  const { data, ...props } = useListQuery({
    eventId: String(event.id),
    types: JSON.stringify([
      PayoutType.Reimbursements,
      PayoutType.PrepaidReimbursements,
    ]),
  });

  const filteredValues = useFilter<Payout>({
    query: debounceQuery,
    data: data?.reimbursements || [],
    paths: [
      'date',
      'notes',
      'amount',
      'event.name',
      'category.name',
      'employee.user.name',
    ],
  });

  const canCreateReimbursement = authUserRole && (isManager || isCompanyOwner);

  const { visibleData, ...showMoreProps } = useShowMore(filteredValues);
  const { template, currentTitles } = getTemplateAndTitles(
    canApproveReimbursement
  );

  return (
    <>
      <CommonStyles.StyledList
        {...mapQuery(props)}
        {...search}
        {...showMoreProps}
        count={visibleData?.length}
        header={<ListHeader template={template} titles={currentTitles} />}
        searchBarRightSlot={
          canCreateReimbursement ? (
            <CreateReimbursement event={event} />
          ) : undefined
        }
      >
        {visibleData?.map((value) => {
          const values = [
            formatDate(value?.date),
            value?.employee?.user?.name,
            value?.notes,
            formatPrice(value?.amount),
            value?.category?.name,
            <PayoutStatusBadge type={value?.type} status={value?.status} />,
          ];

          const isPending = value?.status === PayoutStatus.Pending;

          if (
            canApproveReimbursement &&
            canDeleteReimbursementStatuses.includes(value?.status)
          ) {
            values.push(
              <Flex gap={8}>
                {isPending && <ApprovalPayoutDialog data={value} />}
                <RemovePayoutDialog data={value} withoutNavigate />
              </Flex>
            );
          }

          return (
            <ListCard
              padding="16px"
              key={value.id}
              values={values}
              template={template}
              headers={currentTitles}
              onClick={() => setSelectedReimbursementId(value.id)}
            />
          );
        })}
      </CommonStyles.StyledList>

      <ReimbursementDrawerDetails
        selectedReimbursementId={selectedReimbursementId}
        onClose={() => setSelectedReimbursementId(undefined)}
      />
    </>
  );
};
